import { useAppConfig } from '#/config/AppConfigContext';
import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18N_LOCALES } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { listingApiIsomorphicFetchOrg } from '#/modules/listing/orgs/api/listingApiFetchOrg';
import { UnpublishedOrgPageLoadable } from '#/modules/listing/orgs/pages/unpublished/UnpublishedOrgPageLoadable';
import type { viewOrgReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { userDashboardApiIsomorphicFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import type { HorizonAppRouteShouldRevalidateFunctionArgs } from '#/routing/types/HorizonAppRouteShouldRevalidateFunctionArgs';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewOrgPageLoadable } from './ViewOrgPageLoadable';

export const handle = {
  defaultSearchType: 'ORG',
  showSearchRefinements: true,
  showDesktopSearch: true,
} satisfies LayoutHandleProps;

export function shouldRevalidate({
  currentUrl,
  nextUrl,
}: HorizonAppRouteShouldRevalidateFunctionArgs<typeof viewOrgReferenceRoute>) {
  return currentUrl.pathname !== nextUrl.pathname;
}

export async function loader({
  request,
  context,
  params: { orgIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewOrgReferenceRoute>) {
  // It's possible this loadable won't be used if the job is unpublished
  // but in most cases optimistically loading it is best
  await ViewOrgPageLoadable.load();

  const [orgId] = orgIdDashSlug.split('-');

  const org = await listingApiIsomorphicFetchOrg({ request, context })(orgId);

  const fetchedListingExtras =
    await userDashboardApiIsomorphicFetchListingExtras({
      request,
      context,
    })([orgId]);

  const saved = Boolean(fetchedListingExtras[org.id]?.saved);

  if (!org) return idealistNotFound(null);

  if (org.type === 'UNPUBLISHED_ORG') {
    return idealistNotFound({
      type: 'UNPUBLISHED_ORG',
      unpublishedOrg: org,
      handle: {
        hideFooterDivider: 'hide-footer-divider-if-logged-out',
      } satisfies LayoutHandleProps,
    } as const);
  }

  return {
    type: 'ORG',
    org,
    saved,
    locale: context.locale,
    handle: {
      hideFooterDivider: 'hide-footer-divider-if-logged-out',
      searchFromPageType: 'LISTING',
      urlByLocale: org.url,
    } satisfies LayoutHandleProps,
  } as const;
}

export default function ViewHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();

  switch (loaderData?.type) {
    case undefined:
      return <NotFoundPageLoadable />;
    case 'ORG':
      return (
        <RouteModule
          title={`${loaderData.org.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['ORG']}`}
          ogImage={imageUrlForOgMeta({
            data: loaderData.org.logo,
            appConfig,
          })}
          description={loaderData.org.descriptionTruncated?.slice(0, 200)}
          linkRels={{
            translations: I18N_LOCALES.map((locale) => {
              const url = loaderData.org.url[locale];
              if (url) return { url, locale };
            }).filter(Boolean),
            canonical: loaderData.org.url[loaderData.org.locale],
          }}
          schemaMarkups={[
            loaderData.org.schemaOrgMarkup?.[loaderData.locale],
            loaderData.org.schemaBreadcrumbListMarkup?.[loaderData.locale],
          ]}
        >
          <ViewOrgPageLoadable
            initialOrg={loaderData.org}
            initiallySaved={loaderData.saved}
            isPublished
            isPreview={false}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_ORG':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <UnpublishedOrgPageLoadable
            unpublishedOrg={loaderData.unpublishedOrg}
          />
        </RouteModule>
      );
  }
}
